import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import VueRecaptcha from 'vue-recaptcha';
import FormRegister from '@auth/components/forms/form-register/form-register.vue';
import store from '@app/store';
import BpcHeaderLanguageSelector from '@shared/layouts/components/header/header-language-selector.vue';

@Component({
  name: 'bpc-login',
  components: {
    'bpc-form-register': FormRegister,
    ValidationObserver,
    'vue-recaptcha': VueRecaptcha,
    BpcHeaderLanguageSelector,
  },
})
export default class Register extends Vue {
  $refs!: {
    form: InstanceType<typeof FormRegister> & { saveEntity(): void };
    recaptcha: InstanceType<typeof VueRecaptcha> & { reset(): void };
  };

  get tenantAlias(): string {
    return this.$clientRouter.getTenantAlias();
  }
  creatingAccount: boolean = false;
  accountCreated: boolean = false;
  showAlertSuccess: boolean = false;
  showAlertDanger: boolean = false;
  alertMessage: string = '';

  recaptchaVerified: boolean = false;
  recaptchaCode: string = null;

  defaultBackground: string = 'background-color: rgba(15, 58, 93, 0.85)';
  public disableAcceptButton = true;

  get background() {
    return this.tenantBackground
      ? `background-image: url("${this.tenantBackground}")`
      : this.defaultBackground;
  }

  get logo() {
    return `background-image: url("${this.tenantLogo}")`;
  }

  get headerRegisterText() {
    return this.customHeaderRegisterText
      ? this.customHeaderRegisterText
      : this.$t('global.header.register_widget.welcome');
  }

  public mounted(): void {
    this.$root.$on('updateDisableAcceptButton', $event => {
      if ($event === true) {
        this.disableAcceptButton = false;
      } else {
        this.disableAcceptButton = true;
      }
    });
  }
  public destroyed(): void {
    this.$root.$off('updateDisableAcceptButton');
  }

  async beforeMount() {
    if (!this.selectedUserLang && this.lang !== this.defaultLanguage) {
      await this.translationService.getTranslations(this.defaultLanguage);
    }
  }

  createAccount() {
    this.creatingAccount = true;
    this.hideAlert();
    this.$refs.form.saveEntity();
  }

  get withRecaptcha(): boolean {
    return !!process.env.VUE_APP_RECAPTCHA_SITE_KEY;
  }

  get sitekey(): string {
    return process.env.VUE_APP_RECAPTCHA_SITE_KEY || '';
  }

  get lang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  showAlert(status, message) {
    this.alertMessage = message;
    this.showAlertSuccess = status === 'success';
    this.showAlertDanger = status === 'danger';
  }

  hideAlert() {
    this.alertMessage = '';
    this.showAlertSuccess = false;
    this.showAlertDanger = false;
  }

  isEmpty(value) {
    return (
      (typeof value == 'string' && !value.trim()) || typeof value == 'undefined' || value === null
    );
  }

  onVerify(response) {
    this.recaptchaCode = response;
    this.recaptchaVerified = true;
  }

  onExpired() {
    this.recaptchaVerified = false;
    this.recaptchaCode = null;
  }

  onResetRecaptcha() {
    if (this.withRecaptcha) {
      this.creatingAccount = false;
      this.recaptchaVerified = false;
      this.recaptchaCode = null;
      this.$refs.recaptcha.reset();
      this.$store.commit('auth/restartedRecaptcha');
    }
  }

  onEntityErrorSaving(e) {
    this.$store.commit('auth/restartRecaptcha');
    this.creatingAccount = false;
  }

  onEntitySaved(e) {
    this.showAlert('success', e.message);
    this.accountCreated = true;
    this.creatingAccount = false;
  }
  get settingsLoaded() {
    return !this.$store.getters['centerSettings/isLoading'];
  }

  get tenantBackground() {
    if (
      this.$store.getters['centerSettings/centerSettings'] &&
      this.$store.getters['centerSettings/centerSettings'].background
    ) {
      let background = this.$store.getters['centerSettings/centerSettings'].background;
      if (typeof background === 'string') {
        background = JSON.parse(background);
      }
      return background.url;
    }
    return null;
  }
  get tenantLogo() {
    if (
      this.$store.getters['centerSettings/centerSettings'] &&
      this.$store.getters['centerSettings/centerSettings'].logo_url
    ) {
      let logo = this.$store.getters['centerSettings/centerSettings'].logo_url;
      if (logo && typeof logo === 'string') {
        logo = JSON.parse(logo);
      }
      return logo.url;
    }
    return null;
  }
  get customHeaderRegisterText() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].header_register_text;
    }
    return null;
  }
  get availableLanguages() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].available_languages;
    }
    return null;
  }
  get allowClientRegistration() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].allow_client_registration;
    }
    return null;
  }
  get selectedUserLang() {
    return this.$store.getters['auth/userLang'];
  }
  get defaultLanguage() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/defaultLanguage'];
    }
    return null;
  }
  get allowAccessPublicWebpage() {
    return this.$store.getters['centerSettings/centerSettings']?.allow_access_public_website;
  }
}
